/**
 * Story Browser - Column 1 of workspace
 * Story 198: View and filter stories in backlog
 * Story 227: Add story creation
 * Story 356: Web event instrumentation
 *
 * Features:
 * - Search box with debounce
 * - Status filter dropdown
 * - Stories/Epics view toggle (ViewTabs)
 * - Story list or epic tree based on view
 */

import { useMemo, useCallback, useEffect, useRef, type ReactNode } from 'react';
import { track } from '../../lib/analytics';
import { useNavigate } from 'react-router-dom';
import { useStories, type StoryFilters } from '../../hooks/useStories';
import { useEpics } from '../../hooks/useEpics';
import { useCreateEpic } from '../../hooks/useEpicMutations';
import { useCreateStory } from '../../hooks/useStoryMutations';
import { useTabFromUrl } from '../../hooks/useTabFromUrl';
import { SearchBox } from './SearchBox';
import { StatusFilter } from './StatusFilter';
import { ViewTabs } from './ViewTabs';
import { StoryBacklog } from './StoryBacklog';
import { ProjectSettingsCog } from './ProjectSettingsCog';
import { StoryTree } from './StoryTree';
import { HiddenResultsNotice } from './HiddenResultsNotice';
import './StoryBrowser.css';

/** Convert title to slug format */
function titleToSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .substring(0, 50);
}

interface StoryBrowserProps {
  projectSlug: string;
  selectedStoryId: string | null;
  onSelectStory: (storyExternalId: string) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  statusFilter: string[];
  onStatusChange: (statuses: string[]) => void;
  /** Story 564: opens the project settings page. */
  onOpenSettings: () => void;
  /** Story 546: shown above the header while the project's AI Service checks are off; the page supplies it. */
  alert?: ReactNode;
}

export function StoryBrowser({
  projectSlug,
  selectedStoryId,
  onSelectStory,
  searchQuery,
  onSearchChange,
  statusFilter,
  onStatusChange,
  onOpenSettings,
  alert,
}: StoryBrowserProps) {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useTabFromUrl();

  const filters: StoryFilters = useMemo(
    () => ({
      status: statusFilter.length > 0 ? statusFilter : undefined,
      search: searchQuery || undefined,
    }),
    [statusFilter, searchQuery]
  );

  const { data: storiesData, isLoading: storiesLoading, error: storiesError } = useStories(
    projectSlug,
    filters
  );
  const stories = storiesData?.stories ?? [];
  const hiddenByFilter = storiesData?.hiddenByFilter ?? 0;

  // Story 356: Track search execution when search completes with results
  const lastTrackedQueryRef = useRef<string>('');
  useEffect(() => {
    // Only track when:
    // 1. There's a non-empty search query
    // 2. Stories have finished loading
    // 3. We haven't already tracked this exact query
    if (searchQuery && !storiesLoading && searchQuery !== lastTrackedQueryRef.current) {
      track({
        name: 'search_executed',
        properties: { query: searchQuery, result_count: stories.length },
      });
      lastTrackedQueryRef.current = searchQuery;
    }
    // Reset tracking when query is cleared
    if (!searchQuery) {
      lastTrackedQueryRef.current = '';
    }
  }, [searchQuery, storiesLoading, stories.length]);

  const { data: epics, isLoading: epicsLoading, error: epicsError } = useEpics(projectSlug);
  const createEpic = useCreateEpic();
  const createStory = useCreateStory();

  const handleSearchChange = useCallback((value: string) => {
    onSearchChange(value);
  }, [onSearchChange]);

  const handleCreateStory = useCallback(async () => {
    const title = window.prompt('Story title:');
    if (title?.trim()) {
      const result = await createStory.mutateAsync({
        projectSlug,
        title: title.trim(),
        afterStoryId: selectedStoryId || undefined,
      });
      if (result?.externalId) {
        onSelectStory(result.externalId);
      }
    }
  }, [projectSlug, selectedStoryId, createStory, onSelectStory]);

  const handleCreateEpic = useCallback(() => {
    const title = window.prompt('Epic title:');
    if (title?.trim()) {
      const slug = titleToSlug(title.trim());
      createEpic.mutate({ projectSlug, slug, title: title.trim() });
    }
  }, [projectSlug, createEpic]);

  const handleStatusChange = useCallback((statuses: string[]) => {
    onStatusChange(statuses);
  }, [onStatusChange]);

  const handleNavigateHome = useCallback(() => {
    navigate('/dashboard');
  }, [navigate]);

  const isLoading = storiesLoading || epicsLoading;
  const error = storiesError || epicsError;

  return (
    <div className="story-browser">
      {alert}
      <div className="story-browser__header">
        <button
          className="story-browser__home-button"
          onClick={handleNavigateHome}
          type="button"
          aria-label="Go to dashboard"
          title="Dashboard"
        >
          <svg viewBox="0 0 16 16" fill="currentColor">
            <path d="M8.354 1.146a.5.5 0 0 0-.708 0l-6 6A.5.5 0 0 0 1.5 7.5v7a.5.5 0 0 0 .5.5h4.5a.5.5 0 0 0 .5-.5v-4h2v4a.5.5 0 0 0 .5.5H14a.5.5 0 0 0 .5-.5v-7a.5.5 0 0 0-.146-.354L8.354 1.146z" />
          </svg>
        </button>
        {/* Story 564: project settings, reachable whether or not a story is open */}
        <ProjectSettingsCog onOpen={onOpenSettings} />
        <SearchBox
          value={searchQuery}
          onChange={handleSearchChange}
          placeholder="Search stories..."
        />
        <StatusFilter selected={statusFilter} onChange={handleStatusChange} />
      </div>

      <ViewTabs
        activeView={viewMode}
        onViewChange={setViewMode}
        onCreateStory={handleCreateStory}
        onCreateEpic={handleCreateEpic}
      />

      <div className="story-browser__content">
        {isLoading && (
          <div className="story-browser__loading">Loading...</div>
        )}

        {error && (
          <div className="story-browser__error">
            Failed to load: {error.message}
          </div>
        )}

        {!isLoading && !error && viewMode === 'backlog' && (
          <>
            <StoryBacklog
              stories={stories}
              projectSlug={projectSlug}
              selectedStoryId={selectedStoryId}
              onSelectStory={onSelectStory}
              onCreateStory={handleCreateStory}
            />
            <HiddenResultsNotice
              hiddenCount={hiddenByFilter}
              searchQuery={searchQuery}
              statusFilter={statusFilter}
              projectSlug={projectSlug}
              selectedStoryId={selectedStoryId}
              onSelectStory={onSelectStory}
            />
          </>
        )}

        {!isLoading && !error && viewMode === 'epics' && (
          <StoryTree
            stories={stories}
            epics={epics ?? []}
            projectSlug={projectSlug}
            selectedStoryId={selectedStoryId}
            onSelectStory={onSelectStory}
            onCreateStory={handleCreateStory}
            onCreateEpic={handleCreateEpic}
          />
        )}
      </div>
    </div>
  );
}
